<template>
  <div>
    <transition mode="out-in">
      <div v-if="loading" class="d-flex align-items-center justify-content-center" style="height:50vh;"><loading-spinner /></div>
      <div v-else>

        <!-- Stats Cards Row -->
        <div class="row g-3 mb-3">
          <div class="col-lg-3 col-md-6 col-12">
            <div class="stat-card">
              <div class="stat-label">New Listings</div>
              <div class="stat-value">{{ totalItemsListed }}</div>
              <range-comparison v-if="comparisonMode" :curr-val="totalItemsListed" :prev-val="comparisonTotalItemsListed" format="int" :show-comparison-label="true" />
              <div class="stat-icon text-secondary"><i class="bi bi-box-seam"></i></div>
            </div>
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <div class="stat-card">
              <div class="stat-label">Items Sold</div>
              <div class="stat-value">{{ totalItemsSold }}</div>
              <range-comparison v-if="comparisonMode" :curr-val="totalItemsSold" :prev-val="comparisonTotalItemsSold" format="int" />
              <div class="stat-icon text-secondary"><i class="bi bi-bag-check"></i></div>
            </div>
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <div class="stat-card">
              <div class="stat-label">Listing Conversion</div>
              <div class="stat-value">{{ sellThroughRate }}%</div>
              <range-comparison v-if="comparisonMode" :curr-val="parseFloat(sellThroughRate)" :prev-val="comparisonSellThroughRate" format="percent" />
              <div class="stat-icon text-secondary"><i class="bi bi-percent"></i></div>
            </div>
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <div class="stat-card">
              <div class="stat-label">Avg Listed / Day</div>
              <div class="stat-value">{{ avgListedPerDay }}</div>
              <range-comparison v-if="comparisonMode" :curr-val="parseFloat(avgListedPerDay)" :prev-val="comparisonAvgListedPerDay" format="float" />
              <div class="stat-icon text-secondary"><i class="bi bi-calendar-check"></i></div>
            </div>
          </div>
        </div>
        <!-- Chart Row -->
        <div class="row g-3">
          <div class="col-12">
            <div class="chart-card">
              <h6 class="chart-title">Items Listed vs Sold Over Time</h6>
              <apexchart
                  v-if="chartData.series[0].data.length > 0"
                  type="line"
                  height="300"
                  :options="chartData.options"
                  :series="chartData.series"
              />
              <div v-else class="text-muted text-center py-5">No data available for the selected date range</div>
              <div class="text-muted mt-2" style="font-size:0.7rem;">* New listings counts unique items only — re-listings of the same SKU are not included.</div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, defineProps, defineAsyncComponent, watch} from "vue"
import { useStore } from "vuex"

import { DateUtil } from "@/services/dateUtils"
import { DateTime } from "luxon"
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
import RangeComparison from "@/app/components/Utils/RangeComparison.vue";

// ApexCharts component
const apexchart = defineAsyncComponent(() =>
    import('vue3-apexcharts').then(module => module.default || module)
)

const props = defineProps({
  range: {
    type: Object,
  }
})

const store = useStore()
const CHART_COLORS = computed(() => store.state.chartColors)
const message = ref('')
const rawChartData = ref([])
const rawComparisonChartData = ref([])
const loading = ref(false)

const comparisonMode = computed(() => props.range?.comparison || 'prev')

function getComparisonDates() {
  const from = props.range.from
  const to = props.range.to
  if (comparisonMode.value === 'yoy') {
    return {
      from: from?.minus ? from.minus({ years: 1 }) : new Date(new Date(from).setFullYear(new Date(from).getFullYear() - 1)),
      to: to?.minus ? to.minus({ years: 1 }) : new Date(new Date(to).setFullYear(new Date(to).getFullYear() - 1))
    }
  }
  // 'prev' — shift back by range span for non-overlapping periods
  const spanDays = from?.diff
      ? Math.floor(to.startOf('day').diff(from.startOf('day'), 'days').days) + 1
      : Math.floor((new Date(to).setHours(0,0,0,0) - new Date(from).setHours(0,0,0,0)) / 86400000) + 1
  return {
    from: from?.minus ? from.minus({ days: spanDays }) : new Date(new Date(from).getTime() - spanDays * 86400000),
    to: to?.minus ? to.minus({ days: spanDays }) : new Date(new Date(to).getTime() - spanDays * 86400000)
  }
}

function fetchComparisonData() {
  const { from, to } = getComparisonDates()
  const fromDate = dateUtil.value.toISODate(from)
  const toDate = dateUtil.value.toISODate(to)
  store.dispatch("eshop/getItemsListedSold", { fromDate, toDate })
      .then(data => {
        rawComparisonChartData.value = (data.chartData || []).map(item => ({
          ...item,
          items_listed: parseInt(item.items_listed, 10) || 0,
          items_sold: parseInt(item.items_sold, 10) || 0
        }))
      })
      .catch(() => { rawComparisonChartData.value = [] })
}

const comparisonTotalItemsListed = computed(() =>
    rawComparisonChartData.value.reduce((sum, item) => sum + (item.items_listed || 0), 0)
)
const comparisonTotalItemsSold = computed(() =>
    rawComparisonChartData.value.reduce((sum, item) => sum + (item.items_sold || 0), 0)
)
const comparisonSellThroughRate = computed(() => {
  if (comparisonTotalItemsListed.value === 0) return 0
  return (comparisonTotalItemsSold.value / comparisonTotalItemsListed.value) * 100
})
const comparisonAvgListedPerDay = computed(() => {
  if (rawComparisonChartData.value.length === 0) return 0
  return comparisonTotalItemsListed.value / rawComparisonChartData.value.length
})

// Get timezone from store config
const storeConfig = computed(() => store.state.eshop.eshop)
const dateUtil = computed(() => new DateUtil(storeConfig.value?.timezone || store.state.defaultTimezone))

// Computed stats
const totalItemsListed = computed(() => {
  return rawChartData.value.reduce((sum, item) => sum + (item.items_listed || 0), 0)
})

const totalItemsSold = computed(() => {
  return rawChartData.value.reduce((sum, item) => sum + (item.items_sold || 0), 0)
})

const sellThroughRate = computed(() => {
  if (totalItemsListed.value === 0) return 0
  return ((totalItemsSold.value / totalItemsListed.value) * 100).toFixed(1)
})

const avgListedPerDay = computed(() => {
  if (rawChartData.value.length === 0) return 0
  return (totalItemsListed.value / rawChartData.value.length).toFixed(1)
})

// Chart data configuration
const chartData = computed(() => {
  const from = props.range.from
  const to = props.range.to
  const intervalKey = props.range.interval || 'day'

  // Generate buckets based on interval (day/week/month/quarter/year)
  const buckets = dateUtil.value.generateBuckets(from, to, intervalKey)

  // Filter by date range
  const filtered = rawChartData.value.filter(row => {
    const d = dateUtil.value.fromISO(row.date)
    return d >= from && d <= to
  })

  // Group by bucket into map
  const map = new Map()
  filtered.forEach(row => {
    const key = dateUtil.value.getBucketKey(row.date, intervalKey)

    if (!map.has(key)) {
      map.set(key, { items_listed: 0, items_sold: 0 })
    }
    const agg = map.get(key)
    agg.items_listed += (row.items_listed || 0)
    agg.items_sold += (row.items_sold || 0)
  })

  // Return sorted array with bucketed data
  const bucketedData = buckets.map(key => ({
    date: key.iso,
    ...(map.get(key.iso) || { items_listed: 0, items_sold: 0 })
  }))

  // Format dates using Luxon with UTC to avoid timezone conversion shifting dates
  const dates = bucketedData.map(item =>
      DateTime.fromISO(item.date, { zone: 'utc' }).toFormat('MMM d')
  )
  // Keep raw dates for tooltip
  const rawDates = bucketedData.map(item => item.date)
  const itemsListed = bucketedData.map(item => item.items_listed)
  const itemsSold = bucketedData.map(item => item.items_sold)

  // Adjust line width based on data density (matching InsightsOverview pattern)
  const lineWidth = bucketedData.length > 12 ? 3 : 6

  return {
    series: [
      {
        name: 'Items Sold',
        type: 'bar',
        data: itemsSold
      },
      {
        name: 'Items Listed',
        type: 'line',
        data: itemsListed
      }
    ],
    options: {
      chart: {
        type: 'line',
        height: 400,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      stroke: {
        curve: 'smooth',
        width: [0, lineWidth]  // 0 for bars, lineWidth for line
      },
      colors: ['rgb(211, 232, 211)', CHART_COLORS.value.info],  // Green for sold bars, blue for listed line
      dataLabels: {
        enabled: bucketedData.length < 15,
        enabledOnSeries: [1]  // Only enable for line series (Items Listed)
      },
      markers: {
        size: 0  // No dots on the line
      },
      xaxis: {
        type: 'category',
        categories: dates,
        labels: {
          rotate: -45,
          rotateAlways: false
        }
      },
      yaxis: {
        title: {
          text: 'Number of Items'
        },
        labels: {
          formatter: (val) => Math.round(val)
        }
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center'
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          formatter: (value, { dataPointIndex }) => {
            // Use raw date and format with Luxon
            const rawDate = rawDates[dataPointIndex]
            if (!rawDate) return value
            return DateTime.fromISO(rawDate, { zone: 'utc' }).toFormat('ccc, MMM d, yyyy')
          }
        }
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 4
      }
    }
  }
})

// Fetch chart data
function fetchChartData() {
  // Convert Luxon DateTime to ISO date strings for API
  const fromDate = dateUtil.value.toISODate(props.range.from)
  const toDate = dateUtil.value.toISODate(props.range.to)
  loading.value = true

  store.dispatch("eshop/getItemsListedSold", { fromDate, toDate })
      .then(data => {
        // Convert MySQL string values to integers once (more efficient than repeated parseInt)
        rawChartData.value = (data.chartData || []).map(item => ({
          ...item,
          items_listed: parseInt(item.items_listed, 10) || 0,
          items_sold: parseInt(item.items_sold, 10) || 0
        }))
      })
      .catch(error => {
        message.value = error.response?.data?.error || error.message || 'Failed to load chart data'
        console.error('Failed to load chart data:', error)
      })
      .finally(() => {
        loading.value = false
      })
}

watch(() => props.range, () => {
  onDateChange()
}, { deep: true })



// Handle date range change
function onDateChange() {
  fetchChartData()
  fetchComparisonData()
  fetch('/api/events', {
    method: 'POST',
    keepalive: true,
    body: JSON.stringify({
      event_type: 'date_range_changed',
      context: 'ListingPerformance',
      ts: Date.now()
    })
  })
}

// Load data on mount
onMounted(() => {
  fetchChartData()
  fetchComparisonData()
})
</script>

<style lang="scss" scoped>

</style>